import React, { useState, useEffect } from 'react';
import axios from 'axios';

const generateRefNo = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const m = pad(now.getMonth() + 1);
  const d = pad(now.getDate());
  const h = pad(now.getHours());
  const mi = pad(now.getMinutes());
  const s = pad(now.getSeconds());
  const r = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return `REF-${y}${m}${d}-${h}${mi}${s}-${r}`;
};

const API_URL = process.env.REACT_APP_API_URL || '';

interface EmailCommunication {
  id: number;
  date: string;
  sent_to: string;
  subject?: string;
  message: string;
  employee: string;
  delivery_status: 'sent' | 'delivered' | 'failed' | 'pending';
  ref_no: string;
  forward?: string;
}

interface PolicyDocument {
  id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  description: string;
  created_at: string;
  file_size: number;
  document_type: string;
}

interface PolicyEmailTabProps {
  policyId: number;
  clientId: string;
  policyNumber?: string;
  clientName?: string;
  user?: { id?: number; username?: string; name?: string; email?: string };
}

const PolicyEmailTab: React.FC<PolicyEmailTabProps> = ({
  policyId,
  clientId,
  policyNumber = '',
  clientName = '',
  user,
}) => {
  const [emails, setEmails] = useState<EmailCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailCommunication | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string; type: string }[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);

  const [policyDocuments, setPolicyDocuments] = useState<PolicyDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  const [formData, setFormData] = useState({
    sent_to: '',
    create_recipient: '',
    subject: `[${policyNumber}] `,
    message: `Client Name: ${clientName}\nPolicy Number: ${policyNumber}\n\n`,
    selectedDocuments: [] as number[],
  });

  const [recipients, setRecipients] = useState<string[]>([]);
  const [loggedInUser, setLoggedInUser] = useState<string>('System');
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  const MAX_DOCUMENTS = 5;

  useEffect(() => {
    fetchEmails();
    fetchEmailOptions();
    fetchPolicyDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, policyId]);

  useEffect(() => {
    if (user) {
      const userName = user.username || user.name || user.email;
      if (userName) {
        setLoggedInUser(userName);
        return;
      }
    }
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const u = JSON.parse(storedUser);
        setLoggedInUser(u.username || u.name || u.email || 'System');
      } catch {
        setLoggedInUser('System');
      }
    }
  }, [user]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      subject: `[${policyNumber}] `,
      message: `Client Name: ${clientName}\nPolicy Number: ${policyNumber}\n\n`,
    }));
  }, [policyNumber, clientName]);

  const fetchEmails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/email-policies/communications/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEmails(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching policy email communications:', err);
      setError('Failed to load email communications');
      setEmails([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPolicyDocuments = async () => {
    try {
      setIsLoadingDocuments(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/policy-documents/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = Array.isArray(response.data) ? response.data : response.data?.data || [];

      setPolicyDocuments(
        raw.map((doc: any) => ({
          id: doc.id,
          file_name: doc.original_filename || doc.filename || 'document.pdf',
          original_name: doc.original_filename || doc.filename || 'document.pdf',
          file_path: doc.file_path || '',
          description: doc.description || '',
          created_at: doc.created_at || new Date().toISOString(),
          file_size: doc.file_size || 0,
          document_type: 'policy',
        }))
      );
    } catch (err) {
      console.warn('Could not fetch policy documents:', err);
      setPolicyDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const fetchEmailOptions = async () => {
    try {
      setIsLoadingEmails(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const options: { value: string; label: string; type: string }[] = [];

      try {
        const res = await axios.get(`${API_URL}/api/client-emails/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        (res.data.data || []).forEach((e: any) => {
          if (e.email) {
            options.push({ value: e.email, label: `${e.email} (Client)`, type: 'Client' });
          }
        });
      } catch (err) {
        console.warn('Could not fetch client emails:', err);
      }

      try {
        const res = await axios.get(
          `${API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const persons = Array.isArray(res.data) ? res.data : res.data.data || [];
        persons.forEach((p: any) => {
          if (p.email) {
            options.push({
              value: p.email,
              label: `${p.email} (Person Of Interest)`,
              type: 'POI',
            });
          }
        });
      } catch (err) {
        console.warn('Could not fetch persons of interest:', err);
      }

      const seen = new Set<string>();
      const unique = options.filter((o) => {
        const k = o.value.toLowerCase().trim();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      setEmailOptions(unique);
    } catch (err) {
      console.error('Error fetching email options:', err);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const handleDocumentToggle = (docId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.includes(docId)
        ? prev.selectedDocuments.filter((id) => id !== docId)
        : [...prev.selectedDocuments, docId],
    }));
  };

  const handleAddRecipient = () => {
    const email = formData.sent_to.trim();
    if (!email) return setError('Please select an email address');
    if (recipients.includes(email)) return setError('This email is already added');
    setRecipients([...recipients, email]);
    setFormData((p) => ({ ...p, sent_to: '' }));
    setError(null);
  };

  const handleAddCreateRecipient = () => {
    const email = formData.create_recipient.trim();
    if (!email) return setError('Please enter an email address');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Invalid email address');
    if (recipients.includes(email)) return setError('This email is already added');
    setRecipients([...recipients, email]);
    setFormData((p) => ({ ...p, create_recipient: '' }));
    setError(null);
  };

  const handleRemoveRecipient = (email: string) =>
    setRecipients(recipients.filter((r) => r !== email));

  const handleViewEmail = (email: EmailCommunication) => {
    setSelectedEmail(email);
    setShowPreviewModal(true);
  };

  const resetForm = () => {
    setFormData({
      sent_to: '',
      create_recipient: '',
      subject: `[${policyNumber}] `,
      message: `Client Name: ${clientName}\nPolicy Number: ${policyNumber}\n\n`,
      selectedDocuments: [],
    });
    setRecipients([]);
    setUploadProgress(null);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowCreateModal(false);
    setError(null);
    setIsSubmitting(false);
  };

  const handleOpenEmailModal = () => {
    setFormData((prev) => ({ ...prev, selectedDocuments: [] }));
    setRecipients([]);
    setShowCreateModal(true);
    setError(null);
  };

  const handleCreateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const hasRecipient = formData.sent_to.trim() !== '';
    const hasAdded = recipients.length > 0;
    if (!hasRecipient && !hasAdded) return setError('Please select or add at least one recipient');
    if (!formData.subject.trim()) return setError('Subject is required');
    if (!formData.message.trim()) return setError('Message is required');

    const selectedDocs = policyDocuments.filter((d) =>
      formData.selectedDocuments.includes(d.id)
    );
    if (selectedDocs.length > MAX_DOCUMENTS) {
      return setError(`Too many documents selected. Maximum ${MAX_DOCUMENTS} allowed.`);
    }

    const generatedRefNo = generateRefNo();

    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsSubmitting(false);
        return;
      }

      let allRecipients = [...recipients];
      if (formData.sent_to.trim() && !allRecipients.includes(formData.sent_to.trim())) {
        allRecipients.push(formData.sent_to.trim());
      }

      const payload = new FormData();
      payload.append('to', JSON.stringify(allRecipients));
      payload.append('subject', formData.subject.trim());
      payload.append('message', formData.message.trim());
      payload.append('client_id', clientId);
      payload.append('policy_id', String(policyId));
      payload.append('ref_no', generatedRefNo);
      payload.append('employee', loggedInUser);
      payload.append('document_ids', JSON.stringify(formData.selectedDocuments));

      setUploadProgress({ current: 0, total: selectedDocs.length });

      const successfullyDownloaded: any[] = [];

      for (let i = 0; i < selectedDocs.length; i++) {
        const doc = selectedDocs[i];
        if (!doc?.id || !doc.file_path) {
          setUploadProgress((p) => (p ? { ...p, current: p.current + 1 } : null));
          continue;
        }

        try {
          const response = await axios.get(
            `${API_URL}/api/policy-documents/download/${doc.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
              timeout: 30000,
            }
          );

          const blob = response.data;
          if (!(blob instanceof Blob) || blob.size === 0) continue;

          const fileName = doc.original_name || doc.file_name || 'document.pdf';
          payload.append('files', blob, fileName);
          successfullyDownloaded.push(doc);
        } catch (err: any) {
          console.warn(`Failed to download policy document ${doc.id}:`, err.message);
        } finally {
          setUploadProgress((p) => (p ? { ...p, current: p.current + 1 } : null));
        }
      }

      payload.set(
        'documents',
        JSON.stringify(
          successfullyDownloaded.map((d) => ({
            id: d.id,
            file_name: d.file_name,
            original_name: d.original_name,
            file_path: d.file_path,
          }))
        )
      );

      const response = await axios.post(
        `${API_URL}/api/email-policies/send-formdata`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send email');
      }

      const newEmail: EmailCommunication = {
        id: response.data.data?.communicationId ?? Date.now(),
        date: new Date().toISOString(),
        sent_to: allRecipients.join(', '),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        employee: loggedInUser,
        delivery_status: 'sent',
        ref_no: generatedRefNo,
      };

      setEmails([newEmail, ...emails]);

      resetForm();
      setShowCreateModal(false);
      setUploadProgress(null);

      window.dispatchEvent(new Event('refresh-policy-notes'));

      alert(`✅ Email sent successfully to ${allRecipients.length} recipient(s)!`);
    } catch (err: any) {
      console.error('❌ Error sending policy email:', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to send email';
      setError(msg);
      alert(`❌ ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status: EmailCommunication['delivery_status']) => {
    const classes: Record<string, string> = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return classes[status] || classes.pending;
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading email communications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Policy Email Communications</h3>
        <button
          onClick={handleOpenEmailModal}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Send Mail
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {emails.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          No email communications found for this policy.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Sent To', 'Message', 'Employee', 'Status', 'Ref No', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(email.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.sent_to}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {email.message || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.employee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(email.delivery_status)}`}
                    >
                      {email.delivery_status.charAt(0).toUpperCase() + email.delivery_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {email.ref_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleViewEmail(email)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Email Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">From:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.sent_to}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Date:</span>
                  <span className="text-sm text-gray-900 flex-1">
                    {new Date(selectedEmail.date).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Subject:</span>
                  <span className="text-sm text-gray-900 flex-1 font-medium">
                    {selectedEmail.subject || '-'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Employee:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedEmail.employee}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Ref No:</span>
                  <span className="text-sm text-gray-900 flex-1 font-mono">{selectedEmail.ref_no || '-'}</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px] max-h-[300px] overflow-y-auto">
                  {selectedEmail.message || '-'}
                </div>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedEmail(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Send Mail</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateEmail} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                <div className="flex gap-2">
                  <select
                    value={formData.sent_to}
                    onChange={(e) => setFormData({ ...formData, sent_to: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    disabled={isLoadingEmails}
                  >
                    <option value="">Select an email address...</option>
                    {emailOptions.map((o, i) => (
                      <option key={i} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddRecipient}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Create Recipient
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={formData.create_recipient}
                    onChange={(e) => setFormData({ ...formData, create_recipient: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="new@example.com"
                  />
                  <button
                    type="button"
                    onClick={handleAddCreateRecipient}
                    className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({recipients.length})
                </label>
                {recipients.length === 0 ? (
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                    No recipients added yet.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    {recipients.map((email, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(email)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Files</label>
                {isLoadingDocuments ? (
                  <div className="text-gray-500 text-sm py-2">Loading documents...</div>
                ) : policyDocuments.length === 0 ? (
                  <div className="text-gray-500 text-sm py-2">No documents found for this policy.</div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {policyDocuments.map((doc) => (
                      <label
                        key={doc.id}
                        className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.selectedDocuments.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id)}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.document_type} • {formatFileSize(doc.file_size || 0)}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {formData.selectedDocuments.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.selectedDocuments.length} file(s) selected
                  </p>
                )}
              </div>

              {uploadProgress && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading files...</span>
                    <span>
                      {uploadProgress.current} of {uploadProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${uploadProgress.total ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? uploadProgress
                      ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                      : 'Sending...'
                    : 'Send Mail'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyEmailTab;